import type {
  StorageFileRecordAttributesSubmit,
  StorageFileRecordSubmit,
} from 'hb-react/shared/hooks/useStorage/types';
import type { useSubscribeToAblyChannel } from 'hb-react/shared/hooks/useSubscribeToAblyChannel';
import type { AblyTokenType } from 'hb-react/shared/hooks/useSubscribeToAblyChannel/types';

import type { StorageStatus, VirusScanStatus } from './constants';

type SubscribeToAblyChannel = ReturnType<
  typeof useSubscribeToAblyChannel
>['subscribe'];

export interface GlobalStorageFileConfig {
  /** In bytes to match File.size */
  maxFileSize?: number;
  /** When TRUE, files that throw an error during their processing are automatically marked as removed */
  markFileAsRemovedOnError?: boolean;
}

export interface UseGlobalStorageFilesProps {
  initialFiles?: GlobalStorageFile[];
  config?: GlobalStorageFileConfig;
}

export type UseGlobalStorageFiles = (props: UseGlobalStorageFilesProps) => {
  files: GlobalStorageFile[];
  setFiles: React.Dispatch<React.SetStateAction<GlobalStorageFile[]>>;
  addFiles: AddFiles;
  removeFiles: RemoveFiles;
};

export interface UseGlobalStorageFileProps {
  initialFile?: MaybeGlobalStorageFile;
  onSuccess?: (file: MaybeGlobalStorageFile) => void;
  onError?: (file: GlobalStorageFileWithError) => void;
  config?: GlobalStorageFileConfig;
}

export type UseGlobalStorageFile = (props?: UseGlobalStorageFileProps) => {
  file?: MaybeGlobalStorageFile;
  updateFile: UpdateFile;
  removeFile: (file: GlobalStorageFile) => void;
  isProcessing: boolean;
};

// Need to consolidate the backend to use only app/serializers/storage_attachment_serializer.rb
// instead of web/uploads/storage_files or engines/storage/lib/dto/storage/polymorphic_attachment
export interface StorageAttachmentRecord {
  id: number;
  byteSize: number;
  downloadUrl: string;
  filename: string;
  friendUrl: string;
  htmlUrl: string;
  iconUrl: string;
  mediumUrl: string;
  mimeType: string;
  miniUrl: string;
  originalUrl: string;
  previewUrl: string;
  thumbnailUrl: string;
  virusScanStatus: VirusScanStatus;
  wideUrl: string;
  /**
   * @deprecated Use preview_url instead
   */
  fileUrl: string;
  /**
   * @deprecated Use byte_size instead
   */
  size: number;
  /**
   * @deprecated Use filename instead
   */
  fileName: string;
}

export interface StorageAttachmentRecordSnake {
  id: number;
  byte_size: number;
  download_url: string;
  filename: string;
  friend_url: string;
  html_url: string;
  icon_url: string;
  medium_url: string;
  mime_type: string;
  mini_url: string;
  original_url: string;
  preview_url: string;
  thumbnail_url: string;
  virus_scan_status: VirusScanStatus;
  wide_url: string;
  /**
   * @deprecated Use preview_url instead
   */
  file_url: string;
  /**
   * @deprecated Use byte_size instead
   */
  size: number;
  /**
   * @deprecated Use filename instead
   */
  file_name: string;
}

/**
 * @deprecated Use StorageAttachmentRecord instead.
 *
 * Need to consolidate the backend to use only app/serializers/storage_attachment_serializer.rb instead
 * of web/uploads/storage_files or engines/storage/lib/dto/storage/polymorphic_attachment
 */
export interface StorageFileRecord {
  id: number;
  default_url: string; // 'http://aeneas.localhost:3000/storage/593/download'
  file_url: string; // 'Same as default_url. Potential cleanup opportunity
  name: string;
  file_name: string; // Same as name. Potential cleanup opportunity
  mime_type: string; // 'image/png'
  size: number;
  virus_scan_status: VirusScanStatus;
}

/**
 * @deprecated Use StorageAttachmentRecord instead.
 *
 * Need to consolidate the backend to use only app/serializers/storage_attachment_serializer.rb instead
 * of web/uploads/storage_files or engines/storage/lib/dto/storage/polymorphic_attachment
 */
export interface StorageFileRecordFromPolyAttachment {
  id: number;
  download_url: string; // From polymorphic_attachment
  metadata: {
    // From polymorphic_attachment
    size: number;
    scanned: VirusScanStatus;
    filename: string;
    mime_type: string; // 'image/png'
  };
}

export type StorageRecordTypes =
  | StorageAttachmentRecord
  | StorageFileRecord
  | StorageFileRecordFromPolyAttachment;

export interface FileMetadata {
  name: string;
  url: string;
  type: string;
}

export interface GlobalStorageFile {
  /** Used for image preview in hb-react/shared/components/DesignSystem/QuillEditor/Images/index.jsx */
  download_url?: string;
  error?: string | null;
  /** New Files only. */
  file?: File;
  /** Existing Files only. */
  fileMetadata?: FileMetadata;
  /** Existing Files only. Newly uploaded/attached files will have a shrineKey instead */
  id?: number;
  isRemoved?: boolean;
  /** Unique temporary uuid used to track the file in the frontend. */
  key: string;
  /** Tracks the upload progress of the file synchronously with uploadPercentage */
  onProgress?: ProgressHandler;
  /** New files only. Existing files will have an id instead */
  shrineKey?: string;
  storageStatus?: StorageStatus;
  /** Tracks the upload progress of the file synchronously with onProgress */
  uploadPercentage?: number;
  virusScanStatus?: VirusScanStatus;
  virusScanTimeout?: NodeJS.Timeout;
  abortController: AbortController;
}

export interface GlobalStorageFileWithError extends GlobalStorageFile {
  error: string;
}

export interface UpdateFileProps {
  file?: File | null;
  key?: string;
}

interface RemoveFilesProps {
  files: GlobalStorageFile[];
}

export type GlobalStorageFileIdentifiers = Partial<
  Pick<GlobalStorageFile, 'key' | 'shrineKey' | 'id'>
>;

export type UpdateStoredFileType = (
  identifiers: GlobalStorageFileIdentifiers,
  fileOrFunc:
    | Partial<GlobalStorageFile>
    | ((prevFile: GlobalStorageFile) => GlobalStorageFile),
) => void;

export interface VirusScanningMessage {
  data?: {
    key: string;
    scan_status: VirusScanStatus;
  };
}

export type GetAblyVirusScanMessageHandler = (
  updateStoredFile: UpdateStoredFileType,
  markFileAsRemovedOnError?: boolean,
) => (message: VirusScanningMessage) => void;

export type ProgressHandler = (percentage: number) => void;

export interface FileData {
  file: File;
  onProgress?: ProgressHandler;
  key?: string;
}

export interface PresignAndUploadProps {
  controller: AbortController;
  file: File;
  inBO?: boolean;
  key: string;
  networkId?: number | string;
  onProgress?: ProgressHandler;
  updateStoredFile: UpdateStoredFileType;
  virusScanningEnabled: boolean;
}

export type UpdateStoredFile = (
  identifiers: GlobalStorageFileIdentifiers,
  fileOrFunc:
    | Partial<GlobalStorageFile>
    | ((prevFile: GlobalStorageFile) => GlobalStorageFile),
) => void;

export type AddFiles = (
  filesData: FileData[],
) => Promise<PromiseSettledResult<string | null>[]>;

export type RemoveFiles = (props: RemoveFilesProps) => void;

export type UpdateFile = (props: UpdateFileProps) => Promise<string | null>;

interface GetUpdateStoredFilesProps {
  setStoredFiles: React.Dispatch<React.SetStateAction<GlobalStorageFile[]>>;
}

export type GetUpdateStoredFiles = (
  props: GetUpdateStoredFilesProps,
) => UpdateStoredFile;

export enum OwnerType {
  ADMIN = 'admin',
  USER = 'user',
}

interface GetAddFilesProps {
  currentNetworkId?: number | string;
  markFileAsRemovedOnError?: boolean;
  maxFileSize?: number;
  ownerType?: OwnerType;
  setStoredFiles: React.Dispatch<React.SetStateAction<GlobalStorageFile[]>>;
  subscribeToAblyChannel: SubscribeToAblyChannel;
  updateStoredFile: UpdateStoredFile;
  virusScanningEnabled: boolean;
}

export type GetAddFiles = (props: GetAddFilesProps) => AddFiles;

interface GetRemoveFilesProps {
  setStoredFiles: React.Dispatch<React.SetStateAction<GlobalStorageFile[]>>;
}

export type GetRemoveFiles = (props: GetRemoveFilesProps) => RemoveFiles;

export type MaybeGlobalStorageFile = GlobalStorageFile | null | undefined;

interface GetUpdateStoredFileProps {
  setStoredFile: React.Dispatch<React.SetStateAction<MaybeGlobalStorageFile>>;
}

export type GetUpdateStoredFile = (
  props: GetUpdateStoredFileProps,
) => UpdateStoredFile;

interface GetUpdateFileProps {
  abortController?: AbortController;
  config?: GlobalStorageFileConfig;
  currentNetworkId?: number | string;
  ownerType?: OwnerType;
  setIsProcessing: React.Dispatch<React.SetStateAction<boolean>>;
  setStoredFile: React.Dispatch<React.SetStateAction<MaybeGlobalStorageFile>>;
  subscribeToAblyChannel: SubscribeToAblyChannel;
  updateStoredFile: UpdateStoredFile;
  virusScanningEnabled: boolean;
}

export type GetUpdateFile = (props: GetUpdateFileProps) => UpdateFile;

export type GetStorageAttributesFieldName = (fieldName: string) => string;

export type FindImagesInContent = (
  content?: string | null,
) => GlobalStorageFile[];

export type GetMagicKeyUrl = (props: {
  shrineId: string;
  fieldName: string;
}) => string;

export type GetAblyVirusScanChannel = (
  ownerType: OwnerType | undefined,
  ownerId: number | undefined,
) => string;

export type GetAblyTokenType = (ownerType: OwnerType) => AblyTokenType;

export type FileMatchesIdentifiers = (
  file: GlobalStorageFile,
  identifiers: GlobalStorageFileIdentifiers,
) => boolean;

export type IsInLoadingState = (
  file: GlobalStorageFile,
  virusScanningEnabled: boolean,
) => boolean;

export type AreFilesReadyToSubmit = (
  files: GlobalStorageFile[],
  virusScanningEnabled: boolean,
) => boolean;

export type FormatAddedFileForSubmit = (
  gsFile?: MaybeGlobalStorageFile,
) => StorageFileRecordSubmit | null | undefined;

export type FormatAddedFilesForSubmit = (
  files: GlobalStorageFile[],
) => StorageFileRecordSubmit[];

export type FormatRemovedFilesForSubmit = (
  files: GlobalStorageFile[],
) => StorageFileRecordAttributesSubmit[];

interface GetInitialFilesProps {
  files?: StorageRecordTypes[];
  contentHtml?: string;
}

export type GetInitialFiles = (
  props: GetInitialFilesProps,
) => GlobalStorageFile[];

interface AttachFileProps {
  attachableType: string;
  attachableId: number | string;
  fieldName: string;
  file: StorageFileRecordSubmit;
}

export type AttachFile = (props: AttachFileProps) => Promise<any>;

export interface UseGlobalStorageFileParams {
  initialFile?: MaybeGlobalStorageFile;
  /**
   * Invoked when the current GlobalStorageFile has been successfully updated or removed.
   *
   * @param file The newly added GlobalStorageFile object. If the existing file was removed, this will be null.
   * @returns void
   */
  onSuccess?: (file: GlobalStorageFile | null) => void;
  onError?: (file: GlobalStorageFileWithError) => void;
  config?: any;
}

export interface UseGlobalStorageFileReturnType {
  file: MaybeGlobalStorageFile;
  updateFile: (props: UpdateFileProps) => Promise<string | null>;
  isProcessing: boolean;
}
